// Minimal Server-Sent Events parser.
//
// The public API is a single async generator that accepts any ReadableStream
// of `Uint8Array` chunks (e.g. `response.body` from `fetch`) and yields
// `{event?, data}` records — one per complete SSE event. Lines beginning with
// `:` are treated as comments per spec and silently dropped.
//
// We deliberately avoid pulling `eventsource-parser` as a dep to keep the
// client bundle footprint minimal (zod + nanoid only).

export interface SseEvent {
  event?: string;
  id?: string;
  data: string;
}

const LF = 0x0a;

function toText(chunk: Uint8Array): string {
  return new TextDecoder('utf-8', { fatal: false }).decode(chunk, { stream: true });
}

function parseBlock(raw: string): SseEvent | null {
  const ev: SseEvent = { data: '' };
  const dataLines: string[] = [];
  let hasContent = false;

  for (const rawLine of raw.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (line === '') continue;
    if (line.startsWith(':')) continue; // comment
    hasContent = true;

    const colonIdx = line.indexOf(':');
    let field: string;
    let value: string;
    if (colonIdx === -1) {
      field = line;
      value = '';
    } else {
      field = line.slice(0, colonIdx);
      value = line.slice(colonIdx + 1);
      if (value.startsWith(' ')) value = value.slice(1);
    }

    if (field === 'event') ev.event = value;
    else if (field === 'id') ev.id = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (!hasContent) return null;
  ev.data = dataLines.join('\n');
  return ev;
}

/**
 * Async generator over complete SSE events. Chunks arrive byte-by-byte; we
 * split on the `\n\n` (LF LF) event separator and buffer any trailing partial
 * block until the next chunk arrives.
 */
export async function* parseSseStream(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<SseEvent, void, void> {
  const reader = stream.getReader();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      if (!value) continue;

      // Normalise CRLF → LF as we append.
      const text = toText(value).replace(/\r\n/g, '\n');
      buffer += text;

      for (;;) {
        const sep = buffer.indexOf('\n\n');
        if (sep === -1) break;
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseBlock(block);
        if (parsed) yield parsed;
      }
    }

    // Flush any trailing block without a terminal separator.
    if (buffer.length > 0) {
      const parsed = parseBlock(buffer);
      if (parsed) yield parsed;
    }
  } finally {
    reader.releaseLock();
  }
}

/** Utility for tests: build a ReadableStream from a plain string. */
export function stringToStream(payload: string): ReadableStream<Uint8Array> {
  const bytes = new TextEncoder().encode(payload);
  return new ReadableStream<Uint8Array>({
    start(controller): void {
      controller.enqueue(bytes);
      controller.close();
    },
  });
}

/** Utility for tests: build a ReadableStream that emits multiple byte chunks. */
export function chunksToStream(chunks: string[]): ReadableStream<Uint8Array> {
  const encoder = new TextEncoder();
  let i = 0;
  return new ReadableStream<Uint8Array>({
    pull(controller): void {
      if (i >= chunks.length) {
        controller.close();
        return;
      }
      const chunk = chunks[i] ?? '';
      i += 1;
      controller.enqueue(encoder.encode(chunk));
    },
  });
}

// Referenced to keep LF handy for downstream tests wanting raw byte comparisons.
export const SSE_LF = LF;
